import { agenticInstaller } from "@p0security/integrations-agentic-frontend";
import { appInstaller } from "@p0security/integrations-app-frontend";
import { argocdInstaller } from "@p0security/integrations-argocd-frontend";
import { auth0Installer } from "@p0security/integrations-auth0-frontend";
import { awsInstaller } from "@p0security/integrations-aws-frontend";
import { awsMidcInstaller } from "@p0security/integrations-aws-midc-frontend";
import { awsOidcInstaller } from "@p0security/integrations-aws-oidc-frontend";
import { awsRdsInstaller } from "@p0security/integrations-aws-rds-frontend";
import { awsSmInstaller } from "@p0security/integrations-aws-sm-frontend";
import { azureInstaller } from "@p0security/integrations-azure-frontend";
import { clickhouseInstaller } from "@p0security/integrations-clickhouse-frontend";
import { customInstaller } from "@p0security/integrations-custom-frontend";
import { databricksAiInstaller } from "@p0security/integrations-databricks-ai-frontend";
import { databricksInstaller } from "@p0security/integrations-databricks-frontend";
import { datadogAiInstaller } from "@p0security/integrations-datadog-ai-frontend";
import { dockerInstaller } from "@p0security/integrations-docker-frontend";
import { dynamoDbAiInstaller } from "@p0security/integrations-dynamodb-ai-frontend";
import { entraIdInstaller } from "@p0security/integrations-entra-id-frontend";
import { gcloudInstaller } from "@p0security/integrations-gcloud-frontend";
import { gcpCloudSqlInstaller } from "@p0security/integrations-gcp-cloudsql-frontend";
import { gcpServiceAccountInstaller } from "@p0security/integrations-gcp-service-account-frontend";
import { gcpSmInstaller } from "@p0security/integrations-gcp-sm-frontend";
import { gcpWifInstaller } from "@p0security/integrations-gcp-wif-frontend";
import { githubAiInstaller } from "@p0security/integrations-github-ai-frontend";
import { githubInstaller } from "@p0security/integrations-github-frontend";
import { githubRepositoriesInstaller } from "@p0security/integrations-github-repositories-frontend";
import { gitlabAiInstaller } from "@p0security/integrations-gitlab-ai-frontend";
import { googleDriveAiInstaller } from "@p0security/integrations-google-drive-ai-frontend";
import { harborInstaller } from "@p0security/integrations-harbor-frontend";
import {
  Install,
  type InstallProps,
  type InstallSiblings,
} from "@p0security/integrations-installer";
import type { FrontendInstaller } from "@p0security/integrations-installer/types";
import { jiraAiInstaller } from "@p0security/integrations-jira-ai-frontend";
import { launchdarklyInstaller } from "@p0security/integrations-launchdarkly-frontend";
import { lookerAiInstaller } from "@p0security/integrations-looker-ai-frontend";
import { miroAiInstaller } from "@p0security/integrations-miro-ai-frontend";
import { mongodbAtlasAiInstaller } from "@p0security/integrations-mongodb-atlas-ai-frontend";
import { mysqlInstaller } from "@p0security/integrations-mysql-frontend";
import { oktaInstaller } from "@p0security/integrations-okta-frontend";
import { onePasswordInstaller } from "@p0security/integrations-one-password-frontend";
import { oracleInstaller } from "@p0security/integrations-oracle-frontend";
import { pagerdutyAiInstaller } from "@p0security/integrations-pagerduty-ai-frontend";
import { postgresInstaller } from "@p0security/integrations-postgres-frontend";
import { postgresLegacyInstaller } from "@p0security/integrations-postgres-legacy-frontend";
import { rdpInstaller } from "@p0security/integrations-rdp-frontend";
import { redshiftAiInstaller } from "@p0security/integrations-redshift-ai-frontend";
import { salesforceAiInstaller } from "@p0security/integrations-salesforce-ai-frontend";
import { selfHostedInstaller } from "@p0security/integrations-self-hosted-frontend";
import { serviceNowAiInstaller } from "@p0security/integrations-servicenow-ai-frontend";
import { snowflakeAiInstaller } from "@p0security/integrations-snowflake-ai-frontend";
import { Snowflake } from "@p0security/integrations-snowflake-frontend";
import { splunkStackInstaller } from "@p0security/integrations-splunk-stack-frontend";
import { tableauAiInstaller } from "@p0security/integrations-tableau-ai-frontend";
import { tailscaleInstaller } from "@p0security/integrations-tailscale-frontend";
import { zpaInstaller } from "@p0security/integrations-zpa-frontend";
import { integrationRegistry } from "@p0security/shared/integrations/registry";
import type { RegisteredResourceIntegration } from "@p0security/shared/integrations/types";
import {
  type PermissionType,
  isPermissionType,
} from "@p0security/shared/types/permission";
import type { LDFlagSet } from "launchdarkly-react-client-sdk";
import { camelCase } from "lodash";
import type React from "react";
import { Outlet, Route } from "react-router";

import { Page } from "../App/Page.tsx";
import { EntraId } from "./AzureAD/EntraId.tsx";
import { CommandDisplay } from "./CommandDisplay.tsx";
import { fileTransferInstaller } from "./FileTransfer/install/install.tsx";
import { GoogleWorkspace } from "./GoogleWorkspace/GoogleWorkspace.tsx";
import { IntegrationCard } from "./IntegrationCard.tsx";
import { IntegrationsContext } from "./IntegrationsContext.tsx";
import { k8sInstaller } from "./Kubernetes/install/install.tsx";
import { OwnerAlert } from "./OwnerAlert.tsx";
import { sshInstaller } from "./Ssh/install/install.tsx";
import { DeleteModal } from "./components/DeleteModal.tsx";
import { allGaIntegrations } from "./constants.tsx";
import { identityProviderInstaller } from "./identity-provider/installer.tsx";
import { salesforceInstaller } from "./salesforce/installer.tsx";
import { scimInstaller } from "./scim/installer.tsx";

type InstallSpec =
  /** Integration is installed via the install framework */
  | FrontendInstaller<any>
  /** Integration is installed via a bespoke React component
   *
   * Use for OAuth integrations.
   */
  | React.FC
  /** Integration is not installable */
  | undefined;

// `Snowflake` takes `siblings` as a prop instead of importing
// `installSiblings` directly, to avoid a dependency back onto `frontend`.
// `installSiblings` is only read at render time, after it's initialized, so
// the forward reference here is safe.
const SnowflakeInstaller = () => <Snowflake siblings={installSiblings} />;

// NOTE FOR HUMANS AND AGENTS ALIKE: keep keys lexically sorted
export const installerRegistry: Record<PermissionType, InstallSpec> = {
  "1password": onePasswordInstaller,
  "aws-midc": awsMidcInstaller,
  "aws-oidc": awsOidcInstaller,
  "aws-rds": awsRdsInstaller,
  "aws-sm": awsSmInstaller,
  "azure-ad": EntraId,
  "databricks-ai": databricksAiInstaller,
  "datadog-ai": datadogAiInstaller,
  "dynamodb-ai": dynamoDbAiInstaller,
  "entra-id": entraIdInstaller,
  "file-transfer": fileTransferInstaller,
  "gcp-cloudsql": gcpCloudSqlInstaller,
  "gcp-service-account": gcpServiceAccountInstaller,
  "gcp-sm": gcpSmInstaller,
  "gcp-wif": gcpWifInstaller,
  github: githubInstaller,
  "github-ai": githubAiInstaller,
  "github-repositories": githubRepositoriesInstaller,
  "gitlab-ai": gitlabAiInstaller,
  "google-drive-ai": googleDriveAiInstaller,
  "grafana-cloud": scimInstaller,
  "identity-provider": identityProviderInstaller,
  "jira-ai": jiraAiInstaller,
  "looker-ai": lookerAiInstaller,
  "miro-ai": miroAiInstaller,
  "mongodb-atlas-ai": mongodbAtlasAiInstaller,
  "pagerduty-ai": pagerdutyAiInstaller,
  "self-hosted": selfHostedInstaller,
  "servicenow-ai": serviceNowAiInstaller,
  "splunk-stack": splunkStackInstaller,
  "tableau-ai": tableauAiInstaller,
  "zscaler-private-access": zpaInstaller,
  agentic: agenticInstaller,
  app: appInstaller,
  argocd: argocdInstaller,
  auth0: auth0Installer,
  aws: awsInstaller,
  azure: azureInstaller,
  bundle: undefined,
  clickhouse: clickhouseInstaller,
  cloudflare: scimInstaller,
  custom: customInstaller,
  databricks: databricksInstaller,
  docker: dockerInstaller,
  gcloud: gcloudInstaller,
  harbor: harborInstaller,
  k8s: k8sInstaller,
  launchdarkly: launchdarklyInstaller,
  mysql: mysqlInstaller,
  oci: oracleInstaller,
  okta: oktaInstaller,
  pg: postgresLegacyInstaller,
  postgres: postgresInstaller,
  rdp: rdpInstaller,
  "redshift-ai": redshiftAiInstaller,
  salesforce: salesforceInstaller,
  "salesforce-ai": salesforceAiInstaller,
  snowflake: SnowflakeInstaller, // Needs to load Okta groups
  "snowflake-ai": snowflakeAiInstaller,
  ssh: sshInstaller,
  tailscale: tailscaleInstaller,
  workspace: GoogleWorkspace, // Uses OAuth
};

/** Registration is a FrontendInstaller */
const isFrameworkInstall = (
  installer: InstallSpec
): installer is FrontendInstaller<any> =>
  typeof installer === "object" && "components" in installer;

/** Registration is a React component */
const isBespokeInstall = (installer: InstallSpec): installer is React.FC =>
  typeof installer === "function";

// @p0security/integrations-installer knows nothing about the integrations
// *list page* (IntegrationCard, IntegrationsContext, OwnerAlert, the GA
// registry, DeleteModal, CommandDisplay); those live in `frontend` and are
// injected here rather than imported directly by the installer package.
export const installSiblings: InstallSiblings = {
  IntegrationCard,
  IntegrationsContext,
  OwnerAlert,
  gaIntegrations: allGaIntegrations,
  DeleteModal,
  CommandDisplay,
};

export const IntegrationInstaller: React.FC<
  Pick<
    InstallProps<any>,
    "cardOverride" | "componentOverride" | "onInstall"
  > & {
    id: string;
    installer: InstallSpec;
    registration: RegisteredResourceIntegration;
  }
  // Uppercase value so we can dynamically create a React element
> = ({ id, installer: Installer, registration, ...props }) =>
  isFrameworkInstall(Installer) ? (
    <Install
      installer={Installer}
      integration={id}
      // @ts-expect-error we need to ensure that only component installs are registered here
      registration={registration}
      siblings={installSiblings}
      {...props}
    />
  ) : isBespokeInstall(Installer) ? (
    <Installer />
  ) : null;

export const resourceIntegrationRoutes = (flags?: LDFlagSet) =>
  Object.entries(installerRegistry).map((entry) => {
    const [id, installer] = entry;

    if (!isPermissionType(id)) return null;
    const registration = integrationRegistry[id];
    const element = (
      <IntegrationInstaller
        id={id}
        installer={installer}
        registration={registration}
      />
    );
    if (!element) return null;

    return (
      (!registration.flag || flags?.[camelCase(registration.flag)]) && (
        <Route
          key={id}
          path={id}
          element={<Page title={registration.label}>{element}</Page>}
        >
          {typeof registration.components === "object" ? (
            <Route path=":component" element={<Outlet />}>
              <Route path=":id" element={null} />
            </Route>
          ) : null}
        </Route>
      )
    );
  });
